import React, { useState, useEffect, useRef } from 'react';
import './DatePicker.css';

const DatePicker = ({ value, onChange, className = '' }) => {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setMonth(String(date.getMonth() + 1).padStart(2, '0'));
        setDay(String(date.getDate()).padStart(2, '0'));
        setYear(String(date.getFullYear()));
      }
    } else {
      setMonth('');
      setDay('');
      setYear('');
    }
  }, [value]);

  useEffect(() => {
    if (month && day && year) {
      const daysInMonth = new Date(year, month, 0).getDate();
      const validDay = Math.min(parseInt(day), daysInMonth);

      const dateString = `${year}-${month.padStart(2, '0')}-${String(validDay).padStart(2, '0')}`;
      onChange(dateString);
    } else if (!month && !day && !year) {
      onChange('');
    }
  }, [month, day, year, onChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && event.target && event.target instanceof Node && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const getDaysInMonth = () => {
    if (!month || !year) return 31;
    return new Date(year, month, 0).getDate();
  };

  const days = Array.from({ length: getDaysInMonth() }, (_, i) => i + 1);

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setMonth(newMonth);

    if (day && year) {
      const daysInNewMonth = new Date(year, newMonth, 0).getDate();
      if (parseInt(day) > daysInNewMonth) {
        setDay(String(daysInNewMonth).padStart(2, '0'));
      }
    }
  };

  const handleDayChange = (e) => {
    setDay(e.target.value.padStart(2, '0'));
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setYear(newYear);

    if (day && month) {
      const daysInMonth = new Date(newYear, month, 0).getDate();
      if (parseInt(day) > daysInMonth) {
        setDay(String(daysInMonth).padStart(2, '0'));
      }
    }
  };

  const displayValue = month && day && year
    ? `${months.find(m => m.value === month)?.label || ''} ${day}, ${year}`
    : 'Select date';

  return (
    <div className={`date-picker-display ${isOpen ? 'open' : ''} ${className}`} ref={containerRef} onClick={() => setIsOpen(!isOpen)}>
      <span className={!month || !day || !year ? 'placeholder' : ''}>
        {displayValue}
      </span>
      <svg
        className={`date-picker-arrow ${isOpen ? 'open' : ''}`}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <path
          d="M2 4L6 8L10 4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {isOpen && (
        <div className="date-picker-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="date-picker-selects">
            <div className="date-picker-select-group">
              <label>Month</label>
              <select
                value={month}
                onChange={handleMonthChange}
                className="date-picker-select"
              >
                <option value="">Month</option>
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="date-picker-select-group">
              <label>Day</label>
              <select
                value={day}
                onChange={handleDayChange}
                className="date-picker-select"
                disabled={!month || !year}
              >
                <option value="">Day</option>
                {days.map(d => (
                  <option key={d} value={String(d).padStart(2, '0')}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div className="date-picker-select-group">
              <label>Year</label>

              <select
                value={year}
                onChange={handleYearChange}
                className="date-picker-select"
              >
                <option value="">Year</option>
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="date-picker-actions">
            <button
              type="button"
              className="date-picker-clear"
              onClick={() => {
                setMonth('');
                setDay('');
                setYear('');
                setIsOpen(false);
              }}
            >
              Clear
            </button>

            <button
              type="button"
              className="date-picker-done"
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>

          </div>

        </div>

      )}
    </div>

  );
};

export default DatePicker;

